import { canonicalJson, macroPackageUploadRequestSchema, type MacroPackageUploadReply,
  type MacroPackageUploadRequest } from '@playcastdotio/Messaging';
import type { RawPackageUploadClient } from './MacroPackageCaptureRelay';

interface OwnedTransfer {
  identity: string;
  state: 'uploading' | 'complete' | 'failed';
  error?: string;
}

/** Upload-only work owned by the authenticated admin peer; no native recording/input command is issued. */
export class RemoteRawPackageTransferManager {
  private readonly transfers = new Map<string, OwnedTransfer>();
  public constructor(private readonly uploader: RawPackageUploadClient, private readonly hostId: () => string) {}

  public handle(peerId: string, payload: unknown): MacroPackageUploadReply | null {
    const parsed = macroPackageUploadRequestSchema.safeParse(payload);
    if (!parsed.success) return null;
    const request = parsed.data;
    if (request.hostId !== this.hostId()) return this.failed(request, 'raw_package_upload_host_changed');
    const identity = canonicalJson({ hostId: request.hostId, campaignId: request.campaignId, pin: request.pin });
    const previous = this.transfers.get(peerId);
    if (request.command === 'status') {
      if (!previous || previous.identity !== identity) return this.failed(request, 'raw_package_upload_not_owned');
      return this.reply(request, previous);
    }
    if (previous?.state === 'uploading') {
      return previous.identity === identity ? this.reply(request, previous) : this.failed(request, 'raw_package_upload_busy');
    }
    const transfer: OwnedTransfer = { identity, state: 'uploading' };
    this.transfers.set(peerId, transfer);
    try {
      void this.uploader.upload(request.pin.packageId, request.campaignId, request.uploadGrantToken, request.pin.manifestSha256)
        .then(result => {
          transfer.state = result.ok ? 'complete' : 'failed';
          if (!result.ok) transfer.error = boundedError(result.error);
        }, error => { transfer.state = 'failed'; transfer.error = boundedError(error); });
    } catch (error) { transfer.state = 'failed'; transfer.error = boundedError(error); }
    return this.reply(request, transfer);
  }

  public release(peerId: string): void { this.transfers.delete(peerId); }

  private failed(request: MacroPackageUploadRequest, error: string): MacroPackageUploadReply {
    return { requestId: request.requestId, hostId: request.hostId, campaignId: request.campaignId, pin: request.pin, state: 'failed', error };
  }
  private reply(request: MacroPackageUploadRequest, transfer: OwnedTransfer): MacroPackageUploadReply {
    const identity = { requestId: request.requestId, hostId: request.hostId, campaignId: request.campaignId, pin: { ...request.pin } };
    if (transfer.state === 'failed') return { ...identity, state: 'failed', error: transfer.error ?? 'raw_package_upload_failed' };
    const state = transfer.state === 'uploading' ? this.uploader.stage?.(request.pin.packageId, request.campaignId, request.pin.manifestSha256) ?? 'uploading'
      : 'complete';
    return { ...identity, state };
  }
}

function boundedError(error: unknown): string {
  const message = error instanceof Error ? error.message : error;
  return typeof message === 'string' && /^[a-z0-9_]{1,128}$/.test(message) ? message : 'raw_package_upload_failed';
}
